import { Button, Stack, Typography } from '@mui/material';
import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render errors in its subtree and shows a fallback UI instead of
 * letting the whole app unmount to a blank page. Wraps route content in
 * RouteContainer -- a crash on one route (e.g. the table viewer, which has
 * had render crashes on annotation data with unexpected shapes) shouldn't
 * take down the rest of the app.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught a render error:', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          gap={2}
          sx={{ height: '100vh', padding: 4, textAlign: 'center' }}
        >
          <Typography variant="h6">Something went wrong loading this page.</Typography>
          <Typography variant="body2" color="textSecondary">
            {error?.message}
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Reload
          </Button>
        </Stack>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
